import { useWatch } from 'react-hook-form';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormShape } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { RecipientsBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient';
import { PrimaryClientDeathEvent } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { useResiduaryPaysAllTaxes } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/formHooks';
import { isNonTaxableSource } from '@/modules/dispositiveProvisions/utils/taxUtils';
import { DispositiveProvisionTemplateKind } from '@/types/schema';

interface BearsTaxToggleProps {
  /**
   * This field gets passed down from the PrimaryClientDeathSectionRecipients
   * component.
   */
  deathEvent: PrimaryClientDeathEvent | undefined;
  recipientsBasePath: RecipientsBasePath;
  idx: number;
  /**
   * When set, indicates this is a template form (V1) rather than a regular DP form (V2).
   */
  templateType?: DispositiveProvisionTemplateKind;
  /** When true, renders the checkbox without the FormLayoutRow/FormLayoutItem wrapper */
  bare?: boolean;
}

export function BearsTaxToggle({
  deathEvent,
  recipientsBasePath,
  idx,
  templateType,
  bare,
}: BearsTaxToggleProps) {
  const { control } = useFormContext<CombinedFormShape>();
  const dispositiveProvisionsContext = useDispositiveProvisionsContext();

  /**
   * NOTE: This field is only available / relevant in the V2 forms.
   */
  const { residuaryPaysAllTaxes } = useResiduaryPaysAllTaxes({ deathEvent });

  /**
   * For templates, read the residuary setting from the V1 form namespace.
   */
  const templateResiduaryPaysAllTaxes = useWatch({
    control: control as unknown as ReturnType<
      typeof useFormContext<DispositiveProvisionsFormShape>
    >['control'],
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}._residuaryPaysAllTaxes`,
  });

  const isNonTaxable = isNonTaxableSource(dispositiveProvisionsContext);

  /**
   * DP FORMS V2 bearsTax toggle.
   * Only shown if the residuary does NOT pay all taxes for the given death event.
   *
   * Templates only operate on the V1 forms for now, so fall back if a templateType is present
   */
  if (!templateType) {
    if (residuaryPaysAllTaxes || isNonTaxable) return null;
  } else if (templateResiduaryPaysAllTaxes) {
    return null;
  }

  const checkboxElement = (
    <FormAwareCheckbox<CombinedFormShape>
      label="Distribution is net of taxes"
      fieldName={
        `${recipientsBasePath}.${idx}.bearsTax` as const satisfies CombinedPaths
      }
      control={control}
      helpText="If toggled on, recipient will receive distribution specified above less any taxes owed"
    />
  );

  if (bare) {
    return checkboxElement;
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem width={12}>{checkboxElement}</FormLayoutItem>
    </FormLayoutRow>
  );
}
